import React, { useEffect, useState } from "react";
import { PageHeader } from "../../components/Shell";
import { api, formatApiError } from "../../lib/api";
import { cacheEvents } from "../../lib/idb";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";

export default function Events() {
  const [events, setEvents] = useState([]);
  const [show, setShow] = useState(false);
  const [form, setForm] = useState({ name: "", description: "", date: "", location: "", late_after_minutes: 15 });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/events");
      setEvents(data);
      await cacheEvents(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  useEffect(() => {
    load();
  }, []);

  const create = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/events", { ...form, late_after_minutes: Number(form.late_after_minutes) || 15 });
      toast.success("Event created · cached for offline scanner");
      setForm({ name: "", description: "", date: "", location: "", late_after_minutes: 15 });
      setShow(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete event and all attendance for it?")) return;
    try {
      await api.delete(`/events/${id}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return (
    <div data-testid="events-page">
      <PageHeader
        eyebrow="Org Admin"
        title="Events"
        actions={
          <button
            onClick={() => setShow((v) => !v)}
            className="px-4 py-2 rounded-md bg-[#05126E] text-white font-semibold hover:bg-[#030B45] transition-colors flex items-center gap-2"
            data-testid="open-create-event-btn"
          >
            <Plus className="w-4 h-4" /> New event
          </button>
        }
      />

      {show && (
        <form
          onSubmit={create}
          className="mb-8 border border-[#D2D8D4] rounded-md bg-white p-5 grid md:grid-cols-4 gap-3 items-end"
          data-testid="create-event-form"
        >
          <Field label="Name">
            <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
              className="w-full px-3 py-2 border border-[#D2D8D4] rounded-md" data-testid="event-name" />
          </Field>
          <Field label="Date">
            <input type="datetime-local" required value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })}
              className="w-full px-3 py-2 border border-[#D2D8D4] rounded-md" data-testid="event-date" />
          </Field>
          <Field label="Location">
            <input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })}
              className="w-full px-3 py-2 border border-[#D2D8D4] rounded-md" data-testid="event-location" />
          </Field>
          <Field label="Late after (minutes)">
            <input type="number" min={0} max={240} value={form.late_after_minutes}
              onChange={(e) => setForm({ ...form, late_after_minutes: e.target.value })}
              className="w-full px-3 py-2 border border-[#D2D8D4] rounded-md" data-testid="event-late-after" />
          </Field>
          <button type="submit" disabled={busy}
            className="px-4 py-2 rounded-md bg-[#0f1a14] text-white font-semibold disabled:opacity-60 hover:bg-[#1f2a24] transition-colors"
            data-testid="submit-event">
            Create
          </button>
          <Field label="Description">
            <input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
              className="w-full px-3 py-2 border border-[#D2D8D4] rounded-md" data-testid="event-desc" />
          </Field>
        </form>
      )}

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {events.length === 0 && (
          <div className="md:col-span-2 lg:col-span-3 border border-[#E4E4E7] bg-white p-12 text-center text-zinc-500">
            No events yet. Create your first event to start scanning attendance.
          </div>
        )}
        {events.map((e) => (
          <div key={e.id} className="brutal-card border border-[#E4E4E7] bg-white p-5">
            <div className="flex justify-between items-start gap-3">
              <div>
                <div className="text-[10px] font-bold uppercase tracking-[0.25em] text-[#05126E]">
                  {new Date(e.date).toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" })}
                </div>
                <div className="text-xl font-black mt-1">{e.name}</div>
                {e.location && <div className="text-xs text-zinc-500 mt-1">📍 {e.location}</div>}
                {e.description && <div className="text-sm text-zinc-700 mt-2">{e.description}</div>}
              </div>
              <button onClick={() => remove(e.id)}
                className="p-1.5 border border-[#B91C1C] text-[#B91C1C] hover:bg-[#B91C1C] hover:text-white"
                data-testid={`del-event-${e.id}`}>
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
            <div className="mt-4 pt-4 border-t border-[#E4E4E7] flex justify-between items-center text-xs">
              <span className="uppercase tracking-widest text-zinc-500">
                Attendance · late after {e.late_after_minutes ?? 15}m
              </span>
              <span className="text-2xl font-black">{e.attendance_count}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="text-xs font-bold uppercase tracking-widest">{label}</label>
      <div className="mt-1">{children}</div>
    </div>
  );
}
